import { useCallback, useEffect, useState } from "react";
import { fetchSourceModeDiagnostics } from "../../../../../../weather/adminRadarActionsApi";
import WeatherHealthBadge from "../WeatherHealthBadge";

function Row({ label, value, status }) {
  return (
    <div className="weather-radar-fallback__row">
      <span className="weather-radar-fallback__label">{label}</span>
      <span className="weather-radar-fallback__value">{value ?? "—"}</span>
      {status ? <WeatherHealthBadge status={status} /> : null}
    </div>
  );
}

const WeatherValleyPreferredRolloutPanel = ({ onToast }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [diagnostics, setDiagnostics] = useState(null);

  const loadDiagnostics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSourceModeDiagnostics({ refresh: true });
      setDiagnostics(payload);
    } catch (err) {
      setError(err.message || "Unable to load rollout diagnostics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiagnostics();
  }, [loadDiagnostics]);

  const handleCopy = async () => {
    if (!diagnostics) {
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
      onToast?.({ tone: "success", message: "Rollout diagnostics copied." });
    } catch {
      onToast?.({ tone: "error", message: "Unable to copy diagnostics." });
    }
  };

  const rollout = diagnostics?.rollout;
  const siata = diagnostics?.siataHealthSummary;

  return (
    <div className="admin-dash__card panel-card weather-radar-fallback">
      <h2 className="admin-section-title">Valley Preferred Rollout</h2>

      {loading ? <p className="muted">Loading rollout diagnostics…</p> : null}
      {error ? <p className="weather-radar-health-notice">{error}</p> : null}

      {rollout ? (
        <>
          <div className="weather-radar-fallback__grid">
            <Row
              label="Default rollout"
              value={rollout.defaultEnabled ? "Enabled" : "Disabled"}
              status={rollout.defaultEnabled ? "healthy" : "disabled"}
            />
            <Row label="Rollout scope" value={rollout.scope} />
            <Row label="Scope allowed" value={rollout.scopeAllowed ? "Yes" : "No"} />
            <Row
              label="Rollback"
              value={rollout.rollbackActive ? "Active" : rollout.rollbackEnabled ? "Ready" : "Off"}
              status={rollout.rollbackActive ? "fallback_active" : null}
            />
            <Row
              label="SIATA feature"
              value={rollout.siataFeatureEnabled ? "Enabled" : "Disabled"}
            />
            <Row label="Require SIATA healthy" value={rollout.requireSiataHealthy ? "Yes" : "No"} />
            <Row label="Require coverage" value={rollout.requireCoverage ? "Yes" : "No"} />
          </div>

          {siata ? (
            <div className="weather-radar-fallback__sources">
              <Row label="SIATA health" value={siata.status} status={siata.status} />
              <Row label="Renderable frames" value={String(siata.renderableFrameCount ?? "—")} />
            </div>
          ) : null}

          {diagnostics.coverageConfig ? (
            <p className="weather-radar-fallback__summary muted" role="note">
              Coverage bounds:{" "}
              {diagnostics.coverageConfig.boundsConfirmed ? "configured" : "provisional"} —{" "}
              {diagnostics.coverageConfig.note}
            </p>
          ) : null}

          {diagnostics.combinedHealthSummary ? (
            <p className="weather-radar-fallback__summary" role="status">
              Valley Preferred render: {diagnostics.combinedHealthSummary.renderedSource || "—"}
              {diagnostics.combinedHealthSummary.fallbackActive ? " (fallback active)" : ""}
            </p>
          ) : null}

          {diagnostics.rollbackInstructions ? (
            <p className="weather-radar-fallback__summary muted">{diagnostics.rollbackInstructions}</p>
          ) : null}

          {Array.isArray(diagnostics.sampleDecisions) && diagnostics.sampleDecisions.length ? (
            <details className="weather-radar-rollout-samples">
              <summary>Source mode decision examples</summary>
              <ul>
                {diagnostics.sampleDecisions.map((sample) => (
                  <li key={sample.scenario}>
                    <strong>{sample.scenario}</strong> → {sample.decision.sourceMode}
                    {sample.decision.defaultApplied ? " (auto-default)" : ""} — {sample.decision.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="weather-radar-health-actions">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={loadDiagnostics}>
              Refresh
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--secondary"
              onClick={handleCopy}
              disabled={!diagnostics}
            >
              Copy rollout diagnostics
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default WeatherValleyPreferredRolloutPanel;
